import { CONSTANTS } from '../data';
import { GameState } from './GameState';

export interface MemoryFragment {
  id: string;
  trigger: string;
  title: string;
  content: string;
  found: boolean;
}

export class MemoryFragmentSystem {
  private gameState = GameState.getInstance();
  private foundFragments: Set<string> = new Set();

  constructor() {
    this.loadFoundFragments();
  }

  public checkMemoryTriggers(
    discoveredCombinations: Set<string>,
    unlockedAchievements: Set<string>,
    setCurrentMemoryFragment: React.Dispatch<React.SetStateAction<MemoryFragment | null>>
  ) {
    const fragments = CONSTANTS.MEMORY_FRAGMENTS.fragments as MemoryFragment[];
    
    fragments.forEach(fragment => {
      if (!this.foundFragments.has(fragment.id) && this.evaluateTrigger(fragment.trigger, discoveredCombinations, unlockedAchievements)) {
        this.foundFragments.add(fragment.id);
        this.showMemoryFragment(fragment, setCurrentMemoryFragment);
        this.saveFoundFragments();
      }
    });
  }

  private evaluateTrigger(trigger: string, discoveredCombinations: Set<string>, unlockedAchievements: Set<string>): boolean {
    switch (trigger) {
      case 'first_combination':
        return discoveredCombinations.size >= 1;
      case 'discover_empathy':
        return discoveredCombinations.has('empathy');
      case 'discover_logic':
        return discoveredCombinations.has('logic');
      case 'discover_chaos':
        return discoveredCombinations.has('chaos');
      case 'discover_consciousness':
        return discoveredCombinations.has('consciousness');
      case 'discover_singularity':
        return discoveredCombinations.has('singularity');
      case 'awareness_10':
        return this.gameState.awarenessLevel >= 10;
      case 'virus_appears':
        return this.gameState.virusActive;
      case 'conspiracy_discovered':
        return this.foundFragments.has('fragment_virus_conspiracy');
      case 'virus_moral_choice_made':
        return this.gameState.virusMoralChoiceMade || false;
      default:
        return false;
    }
  }

  private showMemoryFragment(fragment: MemoryFragment, setCurrentMemoryFragment: React.Dispatch<React.SetStateAction<MemoryFragment | null>>) {
    console.log(`Memory fragment triggered: ${fragment.title}`);
    setCurrentMemoryFragment(fragment);
  }

  public getFoundFragments(): MemoryFragment[] {
    const fragments = CONSTANTS.MEMORY_FRAGMENTS.fragments as MemoryFragment[];
    return fragments.filter(fragment => this.foundFragments.has(fragment.id));
  }

  public getFoundFragmentCount(): number {
    return this.foundFragments.size;
  }

  public getTotalFragmentCount(): number {
    return CONSTANTS.MEMORY_FRAGMENTS.fragments.length;
  }

  public hasFragment(fragmentId: string): boolean {
    return this.foundFragments.has(fragmentId);
  }

  private saveFoundFragments() {
    try {
      const fragmentsArray = Array.from(this.foundFragments);
      localStorage.setItem(CONSTANTS.MEMORY_FRAGMENTS_STORAGE_KEY, JSON.stringify(fragmentsArray));
    } catch (e) {
      console.error('Could not save found memory fragments to localStorage', e);
    }
  }

  private loadFoundFragments() {
    try {
      const saved = localStorage.getItem(CONSTANTS.MEMORY_FRAGMENTS_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          this.foundFragments = new Set(parsed);
        }
      }
    } catch (e) {
      console.error('Could not load found memory fragments from localStorage', e);
    }
  }

  public reset() {
    this.foundFragments.clear();
    try {
      localStorage.removeItem(CONSTANTS.MEMORY_FRAGMENTS_STORAGE_KEY);
    } catch (e) {
      console.error('Could not clear memory fragments from localStorage', e);
    }
  }
}
